import { create } from "zustand";
import { Folder } from "./types";
import { TreeItemProps } from "@kaltura/ds-react-tree";
import { TruncatedLine } from "@kaltura/mediaspace-shared-ui";
import { baseUrl, getKmsData } from "@kaltura/mediaspace-shared-utils";

interface FoldersState {
    /** array of all folder objects */
    folders: Folder[];
    /** map of folderId to folder object for quick lookup */
    foldersMap: Map<string, Folder>;
    /** hierarchical tree of folder items for TreeView rendering */
    foldersTreeItems: TreeItemProps[];
    /** populates folders, foldersMap, and foldersTreeItems from a flat array */
    setFolders: (folders: Folder[]) => void;
    /** method to refresh the folders list */
    listFolders: () => Promise<void>;
    /** boolean prop to indicate folders list is loading */
    isLoadingFolders: boolean;
}

/**
 * zustand store to handle folder related data and actions
 * shared between the different folders feature components
 */
export const useFoldersStore = create<FoldersState>((set, get) => {
    const initialFolders: Folder[] = [];
    const initialFoldersMap: Map<string, Folder> = new Map();
    const initialTreeItems: TreeItemProps[] = [];

    return {
        folders: initialFolders,
        foldersMap: initialFoldersMap,
        foldersTreeItems: initialTreeItems,
        isLoadingFolders: true,

        setFolders: (folders) => {
            const foldersMap: Map<string, Folder> = new Map();
            const treeItemMap: Map<string, TreeItemProps> = new Map();

            for (const folder of folders) {
                // create folderId => folder map
                foldersMap.set(folder.id, folder);

                // create folderId => TreeItem map
                treeItemMap.set(
                    folder.id,
                    {
                        id: folder.id,
                        label: <TruncatedLine>{folder.name}</TruncatedLine>,
                        subItems: [],
                    }
                );
            }

            const buildTree = (map: Map<string, TreeItemProps>): TreeItemProps[] => {
                const roots: TreeItemProps[] = [];
                for (const folder of folders) {
                    const item = map.get(folder.id);
                    if (!item) continue;

                    if (folder.parentId) {
                        const parent = map.get(folder.parentId);
                        if (parent) {
                            parent.subItems?.push(item);
                        }
                        else {
                            roots.push(item);
                        }
                    }
                    else {
                        roots.push(item);
                    }
                }
                return roots;
            };

            set({
                folders,
                foldersMap,
                foldersTreeItems: buildTree(treeItemMap),
                isLoadingFolders: false,
            });
        },

        listFolders: async () => {
            set({ isLoadingFolders: true });
            const foldersListResult = await getKmsData(`${baseUrl}/folders/folders/list`);
            get().setFolders(foldersListResult);
            set({ isLoadingFolders: false });
        },
    };
});
